import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Default currency decimals used in most currencies.
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision.
 * Mirrors the money-filter precision table used by assets/facets.js so
 * client-side totals match Liquid's `money` filter exactly.
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * Intent to fall back to (Shopify's own recommendation engine, just a less strict
 * matching strategy) when the primary intent comes back with no results.
 */
const FALLBACK_INTENT = { complementary: 'related' };
const PRIMARY_INTENT = 'complementary';

class FrequentlyBoughtTogether extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadSuggestion();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {string | null} */
  #currentVariantId = null;
  /** @type {number} */
  #currentPriceCents = 0;

  /** @type {string | null} */
  #suggestedVariantId = null;
  /** @type {number} */
  #suggestedPriceCents = 0;
  /** @type {boolean} */
  #suggestedAvailable = false;
  /** @type {boolean} */
  #hasSuggestion = false;

  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;
    const resource = event.detail.resource;
    if (!resource) return;

    this.#currentVariantId = String(resource.id);
    this.#currentPriceCents = Number(resource.price) || 0;

    const priceEl = this.querySelector('[data-ref="current-price"]');
    if (priceEl) priceEl.textContent = this.#formatMoney(this.#currentPriceCents);

    this.#updateTotal();
    this.#updateAddButtonState();
  };

  connectedCallback() {
    this.#currentVariantId = this.dataset.currentVariantId || null;
    this.#currentPriceCents = Number(this.dataset.currentPrice) || 0;

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    const addButton = this.querySelector('[data-ref="add-button"]');
    addButton?.addEventListener('click', () => this.#handleAddBoth());

    // The initial page render never has recommendations available (that only happens on a
    // Section Rendering API request), so read the pre-rendered suggestion if a page cache
    // already had one, otherwise kick off the fetch once this block is close to view.
    this.#readSuggestionFromDom();
    if (this.#hasSuggestion) {
      this.#updateTotal();
      this.#updateAddButtonState();
    } else {
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Reads suggestion state (variant id/price/availability) from whatever is currently in
   * the suggested-item slot, whether it was server-rendered on load or injected by a fetch.
   */
  #readSuggestionFromDom() {
    const variantIdInput = this.querySelector('[data-ref="suggested-variant-id"]');
    if (!variantIdInput) {
      this.#hasSuggestion = false;
      return;
    }

    const availableInput = this.querySelector('[data-ref="suggested-variant-available"]');
    this.#suggestedVariantId = variantIdInput.value;
    this.#suggestedAvailable = availableInput ? availableInput.value === 'true' : true;
    this.#hasSuggestion = true;

    const pressedButton = this.querySelector('.fbt-size-button[aria-pressed="true"]');
    this.#suggestedPriceCents = pressedButton
      ? Number(pressedButton.dataset.priceCents ?? 0)
      : Number(this.querySelector('[data-ref="suggested-price"]')?.dataset.priceCents ?? 0);

    this.#bindSizeButtons();
  }

  #bindSizeButtons() {
    this.querySelectorAll('.fbt-size-button').forEach((button) => {
      button.addEventListener('click', () => this.#selectSuggestedVariant(button));
    });
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #selectSuggestedVariant(button) {
    if (button.disabled) return;

    this.querySelectorAll('.fbt-size-button').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    button.setAttribute('aria-pressed', 'true');

    this.#suggestedVariantId = button.dataset.variantId ?? null;
    this.#suggestedAvailable = true;

    const priceEl = this.querySelector('[data-ref="suggested-price"]');
    if (priceEl && button.dataset.price) priceEl.textContent = button.dataset.price;

    // The button's price is already Liquid-formatted (money filter); keep a cents copy
    // for total math by asking the server-rendered price element for its data instead.
    const cents = button.dataset.priceCents;
    if (cents !== undefined) this.#suggestedPriceCents = Number(cents);

    this.#updateTotal();
    this.#updateAddButtonState();
  }

  async #loadSuggestion() {
    const found = await this.#applyIntent(PRIMARY_INTENT);
    if (found) return;

    const fallbackIntent = FALLBACK_INTENT[PRIMARY_INTENT];
    const fallbackFound = fallbackIntent ? await this.#applyIntent(fallbackIntent) : false;
    if (!fallbackFound) this.#hide();
  }

  /**
   * @param {string} intent
   * @returns {Promise<boolean>} whether a suggestion was found and rendered
   */
  async #applyIntent(intent) {
    try {
      const url = `${this.dataset.url}&product_id=${this.dataset.productId}&intent=${intent}`;
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return false;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fetchedSlot = doc.querySelector(`#${this.id} [data-ref="suggested-slot"]`);

      if (!fetchedSlot || !fetchedSlot.querySelector('[data-ref="suggested-variant-id"]')) {
        return false;
      }

      const liveSlot = this.querySelector('[data-ref="suggested-slot"]');
      if (!liveSlot) return false;

      liveSlot.innerHTML = fetchedSlot.innerHTML;
      this.#readSuggestionFromDom();

      const footer = this.querySelector('[data-ref="footer"]');
      if (footer) footer.hidden = false;

      this.#updateTotal();
      this.#updateAddButtonState();
      return true;
    } catch (error) {
      console.error('Frequently bought together error:', error);
      return false;
    }
  }

  #updateTotal() {
    const totalEl = this.querySelector('[data-ref="total-price"]');
    if (!totalEl) return;
    totalEl.textContent = this.#formatMoney(this.#currentPriceCents + this.#suggestedPriceCents);
  }

  #updateAddButtonState() {
    const addButton = this.querySelector('[data-ref="add-button"]');
    if (!addButton) return;

    const ready = Boolean(this.#currentVariantId) && this.#hasSuggestion && this.#suggestedAvailable;
    addButton.disabled = !ready;
    addButton.textContent = this.#hasSuggestion && !this.#suggestedAvailable ? 'Selected size unavailable' : this.dataset.buttonText || addButton.textContent;
  }

  async #handleAddBoth() {
    if (!this.#currentVariantId || !this.#suggestedVariantId) return;

    const addButton = this.querySelector('[data-ref="add-button"]');
    const errorEl = this.querySelector('[data-ref="error"]');
    if (errorEl) errorEl.hidden = true;

    const originalText = addButton?.textContent ?? '';
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = 'Adding…';
    }

    const items = [
      { id: Number(this.#currentVariantId), quantity: 1 },
      { id: Number(this.#suggestedVariantId), quantity: 1 },
    ];

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add items to cart');
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'frequently-bought-together',
          itemCount: items.length,
        })
      );

      this.#trackAddBoth();

      if (addButton) addButton.textContent = 'Added!';
      setTimeout(() => {
        if (!addButton) return;
        addButton.textContent = originalText;
        this.#updateAddButtonState();
      }, 2000);
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
        errorEl.hidden = false;
      }
      if (addButton) addButton.textContent = originalText;
      this.#updateAddButtonState();
    }
  }

  #hide() {
    this.classList.add('hidden');
  }

  /**
   * Pushes an add-to-cart event to window.dataLayer so this specific CTA's usage can be
   * measured independently of the regular add-to-cart button. Pushes both a GA4-shaped
   * `add_to_cart` ecommerce event (so it can feed standard ecommerce reporting if a GTM
   * trigger is set up for it) and a distinctly-named `bundle_add_to_cart` event (so the
   * bundle CTA specifically can be isolated/reported on regardless of that setup).
   * Requires a GTM trigger + tag to actually route this to GA4/Meta/etc. — this only
   * pushes the data, it doesn't configure where it goes.
   */
  #trackAddBoth() {
    const currency = this.querySelector('[data-ref="currency"]')?.dataset.currency || '';
    const currentTitle = this.querySelector('.fbt-item--current .fbt-item-title')?.textContent?.trim() || '';
    const suggestedTitle = this.querySelector('.fbt-item--suggested .fbt-item-title')?.textContent?.trim() || '';

    const items = [
      {
        item_id: this.#currentVariantId,
        item_name: currentTitle,
        price: this.#currentPriceCents / 100,
        quantity: 1,
      },
      {
        item_id: this.#suggestedVariantId,
        item_name: suggestedTitle,
        price: this.#suggestedPriceCents / 100,
        quantity: 1,
      },
    ];
    const value = (this.#currentPriceCents + this.#suggestedPriceCents) / 100;

    window.dataLayer = window.dataLayer || [];
    // Standard GA4 Enhanced Ecommerce shape, in case a GTM trigger is already listening for it.
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: { currency, value, items },
    });
    // Distinct event so this specific bundle widget can be reported on in isolation.
    window.dataLayer.push({
      event: 'bundle_add_to_cart',
      bundle: {
        source: 'frequently-bought-together',
        currency,
        value,
        items,
      },
    });

    // Shopify's own Customer Events pipeline — the same system Shopify's native tracking
    // and most analytics apps (and a Custom Pixel you add in Settings > Customer events)
    // consume. This does NOT create a report inside Admin > Analytics by itself; it just
    // makes the event available to anything subscribed to it.
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish('bundle_add_to_cart', {
        source: 'frequently-bought-together',
        currency,
        value,
        items,
      });
    }
  }

  /**
   * Formats money, replicating the `money` Liquid filter using the shop's real money_format
   * (embedded in a hidden <template>) so this always matches what the rest of the storefront
   * shows, regardless of currency or separator conventions.
   * @param {number} moneyValue - The money value in cents
   * @returns {string}
   */
  #formatMoney(moneyValue) {
    const template = this.querySelector('[data-ref="money-format"]');
    if (!(template instanceof HTMLTemplateElement)) return '';

    const format = template.content.textContent || '{{amount}}';
    const currency = this.querySelector('[data-ref="currency"]')?.dataset.currency || '';

    return format.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      switch (placeholder) {
        case 'amount_no_decimals':
          precision = 0;
          break;
        case 'amount_with_comma_separator':
          thousandsSeparator = '.';
          decimalSeparator = ',';
          break;
        case 'amount_no_decimals_with_comma_separator':
          thousandsSeparator = '.';
          precision = 0;
          break;
        case 'amount_no_decimals_with_space_separator':
          thousandsSeparator = ' ';
          precision = 0;
          break;
        case 'amount_with_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = ',';
          break;
        case 'amount_with_period_and_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = '.';
          break;
        case 'amount_with_apostrophe_separator':
          thousandsSeparator = "'";
          decimalSeparator = '.';
          break;
        default:
          break;
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * @param {number} moneyValue - The money value in cents
   * @param {string} thousandsSeparator
   * @param {string} decimalSeparator
   * @param {number} precision
   * @returns {string}
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);
    let [whole, fraction] = roundedNumber.split('.');
    if (!whole) whole = '0';
    if (!fraction) fraction = '';

    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
